import { Container, LoadingScreen } from '@/shared/components'
import {
  useGetOrganization,
  useUpdateOrganization
} from '@/features/organization/hooks/organizations'
import { useSession } from '@/shared/hooks/useSession'
import { Navigate } from 'react-router-dom'
import { Button, H2, InputGroup, PageSection, NON_EU_COUNTRIES_WITHOUT_POSTCODES } from '@/common'
import useI18n from '@/shared/hooks/usei18n'
import OrganizationAddressForm from './components/OrganizationAddressForm'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Organization,
  OrganizationUpdatePayload,
  organizationAddressSchema,
  organizationContactSchema
} from '@/api/models/OrganizationModel'
import { toast } from 'react-hot-toast'
import OrganizationContactForm from './components/OrganizationContactForm'

type AddressState = {
  street: string
  streetNumber: string
  poBox: string
  city: string
  subDivision: string
  street2: string
  postCode: string
  country: string
}

type ContactState = {
  contactFullName: string
  contactRole: string
  contactEmail: string
  contactPhoneNumber: string
}

type FormState = AddressState & ContactState & { name: string }

const MANDATORY_ADDRESS_FIELD_IDS = [
  'country',
  'city',
  'street',
  'streetNumber',
  'postCode'
] as const satisfies readonly (keyof AddressState)[]

const EMPTY_ADDRESS_STATE: AddressState = {
  street: '',
  streetNumber: '',
  poBox: '',
  city: '',
  subDivision: '',
  street2: '',
  postCode: '',
  country: ''
}

const EMPTY_CONTACT_STATE: ContactState = {
  contactFullName: '',
  contactRole: '',
  contactEmail: '',
  contactPhoneNumber: ''
}

const toFormState = (organization: Organization): FormState => {
  return {
    name: organization.name ?? '',
    street: organization.street ?? '',
    streetNumber: organization.streetNumber ?? '',
    poBox: organization.poBox ?? '',
    city: organization.city ?? '',
    subDivision: organization.subDivision ?? '',
    street2: organization.street2 ?? '',
    postCode: organization.postCode ?? '',
    country: organization.country ?? '',
    contactFullName: organization.contactFullName ?? '',
    contactRole: organization.contactRole ?? '',
    contactEmail: organization.contactEmail ?? '',
    contactPhoneNumber: organization.contactPhoneNumber ?? ''
  }
}

const hasStoredString = (value: string | null | undefined): value is string => {
  return typeof value === 'string' && value.trim().length > 0
}

const countryRequiresPostCode = (country: string): boolean => {
  const normalized = country.trim().toLowerCase()
  if (normalized.length !== 2) {
    return false
  }
  return !NON_EU_COUNTRIES_WITHOUT_POSTCODES.includes(normalized)
}

const diffFormState = (
  baseline: FormState,
  current: FormState
): OrganizationUpdatePayload => {
  const diff: Record<string, string> = {}
  for (const key of Object.keys(current) as (keyof FormState)[]) {
    if (current[key] !== baseline[key]) {
      diff[key] = current[key]
    }
  }
  return diff as OrganizationUpdatePayload
}

export default function OrganizationDetailsPage() {
  const organizationId = useSession().session.organization.id
  const { t } = useI18n()
  const { data: organization, isPending } = useGetOrganization(organizationId)

  const { mutateAsync: updateOrganization, isPending: isOrganizationUpdatePending } =
    useUpdateOrganization(organizationId)

  const [organizationName, setOrganizationName] = useState('')
  const [addressState, setAddressState] = useState<AddressState>(EMPTY_ADDRESS_STATE)
  const [contactState, setContactState] = useState<ContactState>(EMPTY_CONTACT_STATE)
  const [isInitialized, setIsInitialized] = useState(false)

  useEffect(() => {
    if (organization && !isInitialized) {
      const snapshot = toFormState(organization)
      setOrganizationName(snapshot.name)
      setAddressState({
        street: snapshot.street,
        streetNumber: snapshot.streetNumber,
        poBox: snapshot.poBox,
        city: snapshot.city,
        subDivision: snapshot.subDivision,
        street2: snapshot.street2,
        postCode: snapshot.postCode,
        country: snapshot.country
      })
      setContactState({
        contactFullName: snapshot.contactFullName,
        contactRole: snapshot.contactRole,
        contactEmail: snapshot.contactEmail,
        contactPhoneNumber: snapshot.contactPhoneNumber
      })
      setIsInitialized(true)
    }
  }, [organization, isInitialized])

  const onNameChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
    setOrganizationName(evt.target.value)
  }, [])

  const lockedAddressFieldIds = useMemo(() => {
    const locked = new Set<string>()
    if (!organization) {
      return locked
    }
    for (const fieldId of MANDATORY_ADDRESS_FIELD_IDS) {
      const saved = organization[fieldId as keyof Organization]
      if (!hasStoredString(saved)) {
        continue
      }
      if (fieldId === 'postCode') {
        const effectiveCountry = (addressState.country || organization.country || '').trim()
        if (!countryRequiresPostCode(effectiveCountry)) {
          continue
        }
      }
      locked.add(fieldId)
    }
    return locked
  }, [organization, addressState.country])

  const onAddressChange = useCallback((field: { [key: string]: string }) => {
    setAddressState(prev => ({ ...prev, ...field }))
  }, [])

  const mandatoryAddressFieldErrors = useMemo(() => {
    const message = t(
      'settings:organizationDetails.organizationAddress.errors.mandatoryFieldRequired'
    )
    const errors: Partial<Record<(typeof MANDATORY_ADDRESS_FIELD_IDS)[number], string>> = {}
    if (!organization) {
      return errors
    }
    for (const fieldId of MANDATORY_ADDRESS_FIELD_IDS) {
      if (!lockedAddressFieldIds.has(fieldId)) {
        continue
      }
      const current = addressState[fieldId]
      if (!hasStoredString(current)) {
        errors[fieldId] = message
      }
    }
    return errors
  }, [organization, lockedAddressFieldIds, addressState, t])

  const onContactChange = useCallback((field: { [key: string]: string }) => {
    setContactState(prev => ({ ...prev, ...field }))
  }, [])

  const dirtyPayload = useMemo<OrganizationUpdatePayload>(() => {
    if (!organization) {
      return {}
    }
    const baseline = toFormState(organization)
    const current: FormState = {
      name: organizationName,
      ...addressState,
      ...contactState
    }
    return diffFormState(baseline, current)
  }, [organization, organizationName, addressState, contactState])

  const isUpdateOrganizationAddressEnabled = useMemo(() => {
    return organizationAddressSchema.safeParse({ ...addressState }).success
  }, [addressState])

  const isMandatoryAddressLocksSatisfied = useMemo(() => {
    if (!organization) {
      return true
    }
    for (const fieldId of MANDATORY_ADDRESS_FIELD_IDS) {
      if (!lockedAddressFieldIds.has(fieldId)) {
        continue
      }
      const current = addressState[fieldId]
      if (!hasStoredString(current)) {
        return false
      }
    }
    return true
  }, [organization, lockedAddressFieldIds, addressState])

  const isUpdateOrganizationContactEnabled = useMemo(() => {
    return organizationContactSchema.safeParse(contactState).success
  }, [contactState])

  const isUpdateOrganizationNameEnabled = useMemo(() => {
    return organizationName.trim().length >= 2
  }, [organizationName])

  const isFormPristine = useMemo(() => {
    return Object.keys(dirtyPayload).length === 0
  }, [dirtyPayload])

  const isSaveDisabled = useMemo(() => {
    return (
      !isUpdateOrganizationAddressEnabled ||
      !isMandatoryAddressLocksSatisfied ||
      !isUpdateOrganizationContactEnabled ||
      !isUpdateOrganizationNameEnabled ||
      isFormPristine
    )
  }, [
    isUpdateOrganizationAddressEnabled,
    isMandatoryAddressLocksSatisfied,
    isUpdateOrganizationContactEnabled,
    isUpdateOrganizationNameEnabled,
    isFormPristine
  ])

  const onSaveChanges = useCallback(async () => {
    if (isFormPristine) {
      return
    }
    if (!isMandatoryAddressLocksSatisfied) {
      return
    }
    try {
      await updateOrganization(dirtyPayload)
      toast.success(t('settings:organizationDetails.notification.saveChanges'))
    } catch (error) {
      console.error('Error updating contact details:', error)
      toast.error(t('settings:organizationDetails.notification.saveChangesError'))
    }
  }, [
    dirtyPayload,
    isFormPristine,
    isMandatoryAddressLocksSatisfied,
    updateOrganization,
    t
  ])

  if (isPending) {
    return <LoadingScreen />
  }

  if (!organization) {
    return <Navigate to="/dashboard" />
  }

  return (
    <>
      <Container>
        <header>
          <H2>{t('settings:organizationDetails.title')}</H2>
        </header>
        <PageSection title={t('settings:organizationDetails.organizationInfo.title')}>
          <div className="w-1/2 pr-2">
            <InputGroup
              label={t('settings:organizationDetails.organizationInfo.dataSections.name')}
              value={organizationName}
              onChange={onNameChange}
            />
          </div>
        </PageSection>
        <PageSection title={t('settings:organizationDetails.organizationAddress.title')}>
          <OrganizationAddressForm
            initialState={addressState}
            onChange={onAddressChange}
            fieldErrors={mandatoryAddressFieldErrors}
          />
        </PageSection>
        <PageSection title={t('settings:organizationDetails.organizationContact.title')}>
          <OrganizationContactForm initialState={contactState} onChange={onContactChange} />
        </PageSection>
      </Container>
      <footer className="flex justify-end sticky bottom-0 bg-white p-2.5 border-y">
        <Button
          className="w-48"
          onClick={onSaveChanges}
          isLoading={isOrganizationUpdatePending}
          disabled={isSaveDisabled}
        >
          {t('common:actions.save')}
        </Button>
      </footer>
    </>
  )
}
